import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { TemperatureGauge } from '@/modules/ezparcel/components/coldchain/TemperatureGauge';
import { TemperatureChart } from '@/modules/ezparcel/components/coldchain/TemperatureChart';
import { ExcursionAlert } from '@/modules/ezparcel/components/coldchain/ExcursionAlert';
import {
  Thermometer,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Wifi,
  TrendingUp,
  ArrowRight,
  Snowflake,
  ThermometerSun,
  Radio,
  Loader2 } from
'lucide-react';

const API_URL = API_BASE;

export const ColdChainDashboard = () => {const { t } = useTranslation("ezparcel");
  const [stats, setStats] = useState({ active_sensors: 0, total_shipments: 0, active_excursions: 0, compliance_rate: 99.5 });
  const [sensors, setSensors] = useState([]);
  const [excursions, setExcursions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('ezerpToken');
        const headers = { 'Authorization': `Bearer ${token}` };

        const [statsRes, sensorsRes, alertsRes] = await Promise.all([
        fetch(`${API_URL}/api/coldchain/dashboard`, { headers }),
        fetch(`${API_URL}/api/coldchain/sensors`, { headers }),
        fetch(`${API_URL}/api/coldchain/alerts?limit=10`, { headers })]
        );

        if (statsRes.ok) setStats(await statsRes.json());
        if (sensorsRes.ok) {
          const data = await sensorsRes.json();
          setSensors(Array.isArray(data) ? data : data.sensors || []);
        }
        if (alertsRes.ok) {
          const data = await alertsRes.json();
          setExcursions(Array.isArray(data) ? data : data.alerts || []);
        }
      } catch (err) {
        console.error('Failed to fetch cold chain data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const activeExcursions = excursions.filter((e) => e.status === 'Active' || !e.acknowledged);
  const activeSensors = sensors.filter((s) => s.status === 'Active' || s.is_active);
  const warningSensors = sensors.filter((s) => s.status === 'Warning' || s.in_excursion);
  const excursionCards = activeExcursions.slice(0, 2).map((excursion) => ({
    id: excursion.id,
    type: excursion.alert_type || 'Temperature Excursion',
    severity: excursion.severity === 'critical' ? 'Critical' : 'Warning',
    sensorName: excursion.sensor_name || excursion.sensor_id || 'Sensor',
    sensorId: excursion.sensor_id || String(excursion.id),
    status: excursion.acknowledged ? 'Acknowledged' : 'Active',
    currentTemp: Number(excursion.current_temp ?? excursion.temperatures?.max ?? 0),
    threshold: {
      min: Number(excursion.limits?.min ?? excursion.limit_min ?? 2),
      max: Number(excursion.limits?.max ?? excursion.limit_max ?? 8)
    },
    duration: Number(excursion.duration_minutes ?? 0),
    location: excursion.location || 'Cold chain route',
    shipmentId: excursion.shipment_id || 'Unassigned',
    shipmentName: excursion.shipment_name || 'Temperature monitored shipment',
    product: excursion.product || excursion.message || 'Temperature-sensitive goods',
    acknowledgedBy: excursion.acknowledged_by,
    acknowledgedAt: excursion.acknowledged_at
  }));
  const sensorCards = sensors.map((sensor) => ({
    id: sensor.sensor_id || sensor.id,
    status: sensor.status || (sensor.in_excursion ? 'Warning' : sensor.is_active ? 'Active' : 'Offline'),
    lastReading: { temp: Number(sensor.current?.temperature ?? sensor.current_temp ?? 0) },
    assignedTo: { name: sensor.name || sensor.location || 'Unassigned' }
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="coldchain-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <Snowflake className="h-8 w-8 text-cyan-600" />{t("coldChainMonitor", "Cold Chain Monitor")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("realtimeTemperatureMonitoring", "Real-time temperature monitoring and excursion management")}

          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/ezparcel/coldchain/sensors">
            <Button variant="outline" className="gap-2">
              <Radio className="h-4 w-4" />{t("manageSensors", "Manage Sensors")}

            </Button>
          </Link>
          <Link to="/ezparcel/coldchain/excursions">
            <Button className="gap-2 bg-cyan-600 hover:bg-cyan-700">
              <AlertTriangle className="h-4 w-4" />{t("viewAllExcursions", "View All Excursions")}

            </Button>
          </Link>
        </div>
      </div>

      {/* Active Excursions Alert */}
      {activeExcursions.length > 0 &&
      <div className="bg-red-50 dark:bg-red-950/30 border-2 border-red-500 rounded-lg p-4 animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <div>
                <p className="font-bold text-red-700 dark:text-red-400">
                  {activeExcursions.length}{t("activeExcursion", "Active Excursion")}{activeExcursions.length > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-red-600">{t("immediateAttentionRequired", "Immediate attention required")}</p>
              </div>
            </div>
            <Link to="/ezparcel/coldchain/excursions">
              <Button variant="destructive" size="sm">{t("viewDetails", "View Details")}

              <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      }

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t("activeSensors", "Active Sensors")}</p>
                <p className="text-3xl font-bold mt-2">{stats.active_sensors || activeSensors.length}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {warningSensors.length}{t("warnings", "warnings")}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-cyan-100 text-cyan-600">
                <Wifi className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t("activeShipments", "Active Shipments")}</p>
                <p className="text-3xl font-bold mt-2">{stats.total_shipments || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">{t("beingMonitored", "Being monitored")}</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
                <Thermometer className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated border-red-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t("currentExcursions", "Current Excursions")}</p>
                <p className="text-3xl font-bold mt-2 text-red-600">{stats.active_excursions || activeExcursions.length}</p>
                <p className="text-sm text-red-600 mt-1">{t("actionRequired", "Action required")}</p>
              </div>
              <div className="p-3 rounded-xl bg-red-100 text-red-600">
                <AlertTriangle className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t("complianceRate", "Compliance Rate")}</p>
                <p className="text-3xl font-bold mt-2 text-emerald-600">{stats.compliance_rate || 99.5}%</p>
                <p className="text-sm text-muted-foreground mt-1">{t("thisMonth", "This month")}</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-100 text-emerald-600">
                <CheckCircle2 className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Temperature Chart */}
        <Card className="card-elevated lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />{t("temperatureTimeline", "Temperature Timeline")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            <TemperatureChart
              data={[]}
              minThreshold={2}
              maxThreshold={8}
              height={280} />
            
          </CardContent>
        </Card>

        {/* Temperature Gauges */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-muted-foreground" />{t("currentReadings", "Current Readings")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <TemperatureGauge
                value={4.2}
                min={2}
                max={8}
                label="Zone A"
                size="sm" />
              
              <TemperatureGauge
                value={9.1}
                min={2}
                max={8}
                label="Zone B"
                size="sm" />
              
              <TemperatureGauge
                value={-18.5}
                min={-25}
                max={-18}
                label="Freezer"
                size="sm" />
              
              <TemperatureGauge
                value={22.3}
                min={15}
                max={25}
                label="CRT"
                size="sm" />
              
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Excursions */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />{t("activeExcursions", "Active Excursions")}

          </CardTitle>
          <Link to="/ezparcel/coldchain/excursions">
            <Button variant="ghost" size="sm">{t("viewAll", "View All")}

              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 lg:grid-cols-2">
            {excursionCards.map((excursion) =>
            <ExcursionAlert
              key={excursion.id}
              excursion={excursion} />

            )}
            {excursionCards.length === 0 &&
            <p className="text-sm text-muted-foreground">{t("noActiveExcursions", "No active excursions")}</p>
            }
          </div>
        </CardContent>
      </Card>

      {/* Active Sensors Summary */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5 text-muted-foreground" />{t("sensorStatusOverview", "Sensor Status Overview")}

          </CardTitle>
          <Link to="/ezparcel/coldchain/sensors">
            <Button variant="ghost" size="sm">{t("manageSensors", "Manage Sensors")}

              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {sensorCards.map((sensor) =>
            <div
              key={sensor.id}
              className={`p-4 rounded-lg border ${
              sensor.status === 'Active' ? 'bg-emerald-50 border-emerald-200' :
              sensor.status === 'Warning' ? 'bg-amber-50 border-amber-200' :
              'bg-gray-50 border-gray-200'}`
              }>
              
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-xs">{sensor.id}</Badge>
                  <Badge className={
                sensor.status === 'Active' ? 'bg-emerald-100 text-emerald-700' :
                sensor.status === 'Warning' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-700'
                }>
                    {sensor.status}
                  </Badge>
                </div>
                <p className="text-2xl font-bold font-mono text-center">
                  {(sensor.lastReading?.temp ?? 0).toFixed(1)}{t("c", "\xB0C")}
              </p>
                <p className="text-xs text-center text-muted-foreground mt-1">
                  {sensor.assignedTo?.name?.substring(0, 20) || 'Unassigned'}...
                </p>
              </div>
            )}
            {sensorCards.length === 0 &&
            <p className="text-sm text-muted-foreground">{t("noSensorsAvailable", "No sensors available")}</p>
            }
          </div>
        </CardContent>
      </Card>
    </div>);

};
